import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, useHistory } from 'react-router-dom';
import client from '../../lib/apollo/client';
import { AppState } from '../../lib/store';
import { userChipResellerQuery } from '../../lib/utils';
import { gqlTypes } from '../../types/gqlTypes';
import BagStep from './BagStep/BagStep';
import CheckoutStep from './CheckoutStep/CheckoutStep';
import { useInSiteCheckoutEnabled, useMergedSuccessEnabled, useRescueCollarQuestionEnabled } from './flags';
import ContactInfoStep from './ContactInfoStepV2/ContactInfoStepV2';
import EmailStep from './EmailStepV2/EmailStepV2';
import PetInfoStep from './PetInfoStepV2/PetInfoStepV2';
import { checkResellerChipAccessQuery, chipRegistrationAddEmailMutation } from './RegisterChip.graphql';
import RegisterChipContext, { RegisterChipStep, activeStepsInOrder } from './RegisterChipContextV2';
import { NanoRegistrationStep, trackNanoStepCompleted } from '../../lib/analytics/events';

/**
 * Narrows a route step to the four that are form submissions. `upgrade` and `success` are screens
 * and are instrumented by their own events.
 */
function asDataEntryStep(step: RegisterChipStep): NanoRegistrationStep | undefined {
  return step === 'chip' || step === 'email' || step === 'pet' || step === 'contact' ? step : undefined;
}

/**
 * The bag and checkout sit after the confirmation rather than inside the step order.
 *
 * Adding them to `stepsInOrderV2` would make `onStepFinished('success')` push to the bag, which races
 * the "not now" link's own navigation to the chip list, so they get their own routes and guard. With
 * the flag off both redirect to the confirmation rather than 404ing, so a shared or bookmarked link
 * never dead-ends.
 */
function InSiteCheckoutRoutes() {
  const inSiteCheckoutEnabled = useInSiteCheckoutEnabled();

  const guarded = (children: React.ReactNode) => {
    if (!inSiteCheckoutEnabled) {
      return <Redirect to="/register/success" />;
    }
    return children;
  };

  return (
    <>
      <Route exact path="/register/bag" render={() => guarded(<BagStep />)} />
      <Route exact path="/register/checkout" render={() => guarded(<CheckoutStep />)} />
    </>
  );
}
import styles from './RegisterChipV2.module.scss';
import { useRegisterStateV2 } from './RegisterStateV2';
import SerialNumberStep from './SerialNumberStepV2/SerialNumberStepV2';
import SuccessStep from './SuccessStepV2/SuccessStepV2';
import { FeaturesReady } from '@growthbook/growthbook-react';
import UpgradeStep from './UpgradeStepV2/UpgradeStepV2';
import RescueCollarStep from './RescueCollarStep/RescueCollarStep';

/**
 * A helper to determine if all the required information has been filled out for the previous steps
 */
function useMaybeRedirect() {
  const { registerState, setPrimaryEmail, currentUserIsReseller } = useContext(RegisterChipContext);
  const mergedSuccessEnabled = useMergedSuccessEnabled();
  const rescueCollarQuestionEnabled = useRescueCollarQuestionEnabled();
  const steps = activeStepsInOrder(mergedSuccessEnabled, rescueCollarQuestionEnabled);
  const isLoggedIn = useSelector((state: AppState) => !!state.session);
  const email = useSelector((state: AppState) => state.session?.email);

  const { chipId } = registerState;

  return useCallback(
    (step: RegisterChipStep) => {
      if (!chipId && steps.indexOf(step) > steps.indexOf('chip')) {
        return <Redirect to={`/register/chip`} />;
      }

      if (!isLoggedIn && steps.indexOf(step) > steps.indexOf('email')) {
        return <Redirect to={`/register/email`} />;
      }

      if (isLoggedIn && email && step === 'email') {
        setPrimaryEmail(email);
        return <Redirect to={`/register/pet`} />;
      }

      // The upgrade route's own guard then sends resellers and the merged flow on to success.
      if ((currentUserIsReseller || !rescueCollarQuestionEnabled) && step === 'rescueCollar') {
        return <Redirect to={`/register/upgrade`} />;
      }

      if ((currentUserIsReseller || mergedSuccessEnabled) && step === 'upgrade') {
        return <Redirect to={`/register/success`} />;
      }

      return undefined;
    },
    [
      chipId,
      isLoggedIn,
      email,
      setPrimaryEmail,
      currentUserIsReseller,
      mergedSuccessEnabled,
      rescueCollarQuestionEnabled,
      steps,
    ],
  );
}

function RegisterChipRoute({ children, step }: { children: React.ReactNode; step: RegisterChipStep }) {
  const maybeRedirect = useMaybeRedirect();

  return (
    <Route
      exact
      path={`/register/${step}`}
      render={() => {
        return maybeRedirect(step) || children;
      }}
    />
  );
}

export default function RegisterChipV2() {
  const { registerState, ...reducers } = useRegisterStateV2();
  const { chipId } = registerState;
  const rescueCollarQuestionEnabled = useRescueCollarQuestionEnabled();
  const steps = activeStepsInOrder(useMergedSuccessEnabled(), rescueCollarQuestionEnabled);

  const history = useHistory();
  const isLoggedIn = useSelector((state: AppState) => !!state.session);

  const { data: currentUserResellerData, loading: currentUserResellerLoading } = useQuery<gqlTypes.userChipReseller>(
    userChipResellerQuery,
    {
      skip: !isLoggedIn,
    },
  );

  const isReseller = currentUserResellerData?.currentUser.chipReseller?.status === gqlTypes.ResellerStatus.ACCEPTED;

  const onDone = useCallback(() => {
    history.replace('/manage');
  }, [history]);

  const onStepFinished = useCallback(
    (step: RegisterChipStep) => {
      // Every step funnels through here, so one call covers all four data-entry steps and stays
      // correct if the step order changes again.
      const dataEntryStep = asDataEntryStep(step);
      if (dataEntryStep) {
        trackNanoStepCompleted(dataEntryStep);
      }

      const nextStep = steps[steps.indexOf(step) + 1];
      // Resellers skip the rescue collar / upgrade / success steps and go directly to the manage page
      if ((nextStep === 'rescueCollar' || nextStep === 'upgrade' || nextStep === 'success') && chipId && isReseller) {
        history.push(`/vet/chip?id=${chipId}`);
      } else if (nextStep) {
        history.push(`/register/${nextStep}`);
      } else {
        onDone();
      }
    },
    [chipId, history, isReseller, onDone, steps],
  );

  // Mutation to register the chip. It can be called from step 1, if the user is already logged in, or from step 2
  // if the user is not logged in.
  const registerChipAddEmail = useCallback(
    async (chipId: string) => {
      // If user is a reseller, check if this chip is in their stock or they own it.
      // If so, skip this mutation (they should use the reseller assignment flow).
      // If not (Forbidden), proceed with registration which creates a transfer request.
      if (isReseller) {
        try {
          await client.query<gqlTypes.NANO_checkResellerChipAccess, gqlTypes.NANO_checkResellerChipAccessVariables>({
            query: checkResellerChipAccessQuery,
            variables: { id: chipId },
            fetchPolicy: 'network-only',
          });
          // Query succeeded - chip is in reseller's stock or they own it
          // Skip this mutation, they'll use the reseller assignment flow
          return;
        } catch (error) {
          // Check if this is a Forbidden error (chip not in their stock)
          const isForbidden =
            error instanceof Error &&
            (error.message.includes('Forbidden') ||
              (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> }).graphQLErrors?.some(
                (e) => e.extensions?.code === 'FORBIDDEN',
              ));
          if (!isForbidden) {
            // Some other error occurred, rethrow
            throw error;
          }
          // Forbidden error means chip is not in their stock - proceed with registration
        }
      }

      // Leaves a rescue's collar on the adopted pet for the rescue collar step to ask about, rather
      // than returning it to the rescue straight away. Resellers skip that step, so they never defer.
      // Codegen predates the field, so it is added here.
      const response = await client.mutate<
        gqlTypes.NANO_chipRegistrationAddEmail,
        {
          input: gqlTypes.NANO_chipRegistrationAddEmailVariables['input'] & { deferRescueCollarDetach?: boolean };
        }
      >({
        mutation: chipRegistrationAddEmailMutation,
        variables: {
          input: {
            chipId,
            ...(rescueCollarQuestionEnabled && !isReseller ? { deferRescueCollarDetach: true } : {}),
          },
        },
      });

      if (response.data) {
        return;
      }

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      throw new Error('Failed to register chip');
    },
    [isReseller, rescueCollarQuestionEnabled],
  );

  // Wait until we know if the current user, if any, is a reseller
  if (isLoggedIn && currentUserResellerLoading) {
    return null;
  }

  // We want vets to select from a list of stock chips first
  if (isLoggedIn && isReseller && !chipId) {
    return <Redirect to="/vet/chips" />;
  }

  return (
    <RegisterChipContext.Provider
      value={{
        currentUserIsReseller: isReseller,
        onStepFinished,
        registerState,
        registerChipAddEmail,
        ...reducers,
      }}
    >
      <div className={classNames('register-chip-next', styles.container)}>
        <RegisterChipRoute step="chip">
          <SerialNumberStep />
        </RegisterChipRoute>
        <RegisterChipRoute step="email">
          <EmailStep />
        </RegisterChipRoute>
        <RegisterChipRoute step="pet">
          <PetInfoStep />
        </RegisterChipRoute>
        <RegisterChipRoute step="contact">
          <ContactInfoStep />
        </RegisterChipRoute>
        <RegisterChipRoute step="rescueCollar">
          <RescueCollarStep />
        </RegisterChipRoute>
        <RegisterChipRoute step="upgrade">
          <UpgradeStep />
        </RegisterChipRoute>
        <RegisterChipRoute step="success">
          <FeaturesReady>
            <SuccessStep />
          </FeaturesReady>
        </RegisterChipRoute>
        <FeaturesReady>
          <InSiteCheckoutRoutes />
        </FeaturesReady>
      </div>
    </RegisterChipContext.Provider>
  );
}
